import { XMarkIcon } from '@heroicons/react/24/outline';
import { ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import '../paySurge.css';

type Props = {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  /** The design's two widths: 560 for editors, 400 for confirmations. */
  size?: 'md' | 'sm';
  testId?: string;
};

/*
 * The surge dialogs open, oldest first. The confirmation opens over Manage
 * surge pay, and both listen for Escape on the document — so only the one on
 * top answers it. Otherwise one press closed both and threw the draft away.
 */
const openDialogs: symbol[] = [];

/**
 * The surge pay dialogs' shell (GRAVY-10443): Manage surge pay, Override
 * amount and the save confirmation share one frame in the design — white
 * panel, 18px title, close X — so they share one component rather than three
 * copies of the overlay, Escape and focus handling.
 *
 * Portalled to the body so it sits above whatever page opened it, including
 * the Dashboard V2 panel.
 */
const SurgeDialog = ({
  title,
  subtitle,
  onClose,
  children,
  size = 'md',
  testId,
}: Props) => {
  const titleId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Held in a ref so a parent passing an inline handler does not re-run the
  // mount effect — which would pull focus back to the panel on every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const overlay = overlayRef.current;
    const opener = document.activeElement as HTMLElement | null;

    /*
     * Keep focus that lands in this dialog from reaching `document` (GRAVY-10870).
     *
     * A host dialog's focus trap — MUI's "Staff Workers" staffing dialog, for
     * one — listens for `focusin` on the document and pulls focus back into
     * itself whenever it lands outside its own React tree. This dialog is
     * portalled to the body and is not a MUI Modal, so the host still believes
     * it is on top: focus was yanked to `.MuiDialog-container` the moment the
     * "Other" note was clicked, every keystroke went nowhere, and nothing could
     * be typed. Focus inside this dialog is this dialog's business.
     *
     * Registered before the panel focuses itself, so that first focus is kept
     * too. This also hides `focusin` from React's listener, so React `onFocus`
     * / `onBlur` (and MUI inputs, which rely on them) will not fire in here.
     */
    const keepFocus = (e: FocusEvent) => e.stopPropagation();
    overlay?.addEventListener('focusin', keepFocus);

    panelRef.current?.focus();
    const self = Symbol('surgeDialog');
    openDialogs.push(self);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openDialogs[openDialogs.length - 1] === self)
        closeRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      openDialogs.splice(openDialogs.indexOf(self), 1);
      overlay?.removeEventListener('focusin', keepFocus);
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus?.();
    };
  }, []);

  return createPortal(
    <div ref={overlayRef} className="paysurge-overlay" onMouseDown={onClose}>
      <div
        ref={panelRef}
        className={`paysurge-dialog is-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="paysurge-dialoghead">
          <div>
            <h2 className="paysurge-dialogtitle" id={titleId}>
              {title}
            </h2>
            {subtitle && <p className="paysurge-dialogsub">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="paysurge-close"
            aria-label="Close"
            onClick={onClose}
          >
            <XMarkIcon aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default SurgeDialog;
